import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import type { ProviderTask } from '../../models/providerHome';
import { useDismissTask } from '../../api/providerHome/mutations';
import { INK, SUBTLE, TEAL } from './tokens';

const HEALTHIE_CHART_NOTES = 'https://app.gethealthie.com/chart_notes';
const HEALTHIE_PATIENTS = 'https://app.gethealthie.com/clients';

const ReviewLockCard = ({ task }: { task: ProviderTask }) => {
  const patientUrl = `/provider-home/patient/${task.patientId}`;

  return (
    <Flex
      bg="white"
      borderWidth="1px"
      borderColor="cloud"
      borderLeftWidth="3px"
      borderLeftColor={TEAL}
      borderRadius="md"
      p={3}
      gap={3}
      align="flex-start"
      cursor="pointer"
      // TODO production: determine correct navigation target and whether to open in new tab or same tab
      onClick={() => window.open(HEALTHIE_CHART_NOTES, '_blank')}
      transition="filter 0.12s ease"
      _hover={{ filter: 'brightness(0.96)' }}
    >
      <VStack gap={0} flex={1} minW={0} align="stretch">
        <Text fontWeight={600} fontSize="md" color={INK}>
          {task.title}
        </Text>
        {/* Patient name is a secondary CTA — opens the CareOS patient page. */}
        <Text
          color="seafoam"
          fontWeight={500}
          display="inline"
          w="fit-content"
          _hover={{ textDecoration: 'underline' }}
          onClick={(e) => {
            e.stopPropagation();
            window.open(patientUrl, '_self');
          }}
        >
          {task.patientName}
        </Text>
        <Text color={SUBTLE} fontSize="xs">
          {task.meta}
        </Text>
      </VStack>
    </Flex>
  );
};

const NewMemberCard = ({ task }: { task: ProviderTask }) => {
  const { mutate: dismiss, isLoading: isPending } = useDismissTask();
  const patientUrl = `/provider-home/patient/${task.patientId}`;

  return (
    <Flex
      bg="white"
      borderWidth="1px"
      borderColor="cloud"
      borderLeftWidth="3px"
      borderLeftColor={TEAL}
      borderRadius="md"
      p={3}
      gap={3}
      align="flex-start"
      cursor="pointer"
      // TODO production: determine correct navigation target and whether to open in new tab or same tab
      onClick={() => window.open(HEALTHIE_PATIENTS, '_blank')}
      transition="filter 0.12s ease"
      _hover={{ filter: 'brightness(0.96)' }}
    >
      <VStack gap={0} flex={1} minW={0} align="stretch">
        <Text fontWeight={600} fontSize="md" color={INK}>
          {task.title}
        </Text>
        <Text
          color="seafoam"
          fontWeight={500}
          display="inline"
          w="fit-content"
          _hover={{ textDecoration: 'underline' }}
          onClick={(e) => {
            e.stopPropagation();
            window.open(patientUrl, '_self');
          }}
        >
          {task.patientName}
        </Text>
        <Text color={SUBTLE} fontSize="xs">
          {task.meta}
        </Text>
      </VStack>
      {/* Complete — always visible so the action is findable without hovering. */}
      <Box
        as="button"
        aria-label="Complete"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          dismiss(task.id);
        }}
        _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
        aria-disabled={isPending}
        color="seafoam"
        cursor="pointer"
        _hover={{ opacity: 0.7 }}
        transition="opacity 0.15s ease"
        flexShrink={0}
        fontSize="sm"
        fontWeight={500}
        lineHeight="normal"
        mt="2px"
      >
        Complete
      </Box>
    </Flex>
  );
};

export const TaskCard = ({ task }: { task: ProviderTask }) =>
  task.type === 'new_member' ? (
    <NewMemberCard task={task} />
  ) : (
    <ReviewLockCard task={task} />
  );
